import { Button, ButtonProps, YStack, isWeb } from '@my/ui'
import { useEffect, useState } from 'react'
import { useAuth } from 'app/provider/auth'
import { RedeemOutput, useRedeem } from '../../services/redeem'
import { DialogState, RedeemDialog } from '../../components'
import { OfferQueryOutput } from '../../screens/offer-screen/queries'
import { createParam } from 'solito'
import openLink from '../../components/offer-link/lib/handle-link'
import {
  MultipleCodesRedemptionBlockType,
  Redemption,
  SingleCodeRedemptionBlockType,
} from '../../redemptions'
import { useIFrame } from 'app/provider/iframe'
import config from 'app/config'

export interface RedeemOfferButtonProps extends ButtonProps {
  offerData?: OfferQueryOutput
  offer?: {
    id: string
    name: string
    description?: string
    featuredImage?: {
      url: string
    }
    brand: {
      slug: string
      logo?: {
        url: string
      }
    }
    redemptions: Redemption[]
    canRedeem: boolean
    embedUrl?: string
    offerLinkLabel?: string
  }
  isEmbed?: boolean
  buttonProps?: ButtonProps
}

const { useParam } = createParam<{ dialogOpen: string, offerId: string }>()

export const RedeemOfferButton: React.FC<RedeemOfferButtonProps> = ({
  offerData,
  isEmbed,
  buttonProps,
  ...props
}) => {
  const { user } = useAuth()
  const [dialogOpen] = useParam('dialogOpen')
  const [offerId] = useParam('offerId')
  const { microsite } = useIFrame()

  const offer = props.offer || offerData?.Offers.docs[0]!
  const usesCodes = (offer.redemptions || []).some((r) =>
    [MultipleCodesRedemptionBlockType, SingleCodeRedemptionBlockType].includes(r.blockType)
  )

  const isDialogOpen = dialogOpen && (!offerId || offerId === offer.id)

  const { mutate: redeemOffer, loading, data, error } = useRedeem()
  const [embedError, setError] = useState('')
  const [dialogState, setDialogState] = useState(
    isDialogOpen ? DialogState.OpenRedeem : DialogState.Closed
  )
  const [redemptionState, setRedemptionState] = useState<RedeemOutput | undefined>(data ? data : offerData?.fetchRedemption)

  useEffect(() => {
    async function init() {
      if (!isDialogOpen) {
        return
      }

      if (!redemptionState) {
        try {
          const result = await redeemOffer({
            variables: {
              offerId: offer.id,
            },
          })

          console.log('result', result.data.redeemOffer)

          setRedemptionState(result.data.redeemOffer)
        } catch (e) {
          setError(e.message)
        }
      }
    }
    init()
  }, [dialogOpen, redemptionState, redeemOffer, offer.id])

  if (!offer) {
    return null
  }

  const embedUrl = microsite?.embedUrl || offer.embedUrl || config.siteUrl

  const postMessage = (type, payload) => {
    if (!isWeb) return
    parent.postMessage(
      {
        type,
        payload: {
          offer,
          ...payload,
        },
      },
      embedUrl
    )
  }

  const handlePress = async () => {
    if (loading) {
      return
    }

    if (!user?.id && !offer.canRedeem) {
      setDialogState(DialogState.OpenLogin)
      return
    }

    if (!offer.canRedeem && !isEmbed) {
      setDialogState(DialogState.OpenVerify)
      return
    }

    if (!offer.canRedeem && isEmbed) {
      setDialogState(DialogState.OpenRedeem)
      setError('You are not eligible to redeem this offer')
      postMessage('offerRedemptionError', {
        error: 'You are not eligible to redeem this offer',
      })
      return
    }

    try {
      const result = await redeemOffer({
        variables: {
          offerId: offer.id,
        },
      })

      if (result?.data?.redeemOffer?.offerLink && isWeb && !isEmbed) {
        openLink(result.data.redeemOffer.offerLink || '', {
          dialogTab: true,
          queryParams: {
            offerId: offer.id,
          }
        })
      } else if (result?.data?.redeemOffer?.offerLink && isEmbed) {
        postMessage('offerRedeemed', {
          redemption: result.data.redeemOffer,
        })
      } else if (!result?.data?.redeemOffer?.offerLink && isEmbed) {
        postMessage('offerRedemptionError', {
          error: result.data.redeemOffer.message,
        })
      }
      setRedemptionState(result.data.redeemOffer)
    } catch (e) {
      postMessage('offerRedemptionError', {
        error: e.message,
      })
      setError(e.message)

      console.error(e)
    }

    setDialogState(DialogState.OpenRedeem)
  }

  return (
    <YStack width="100%" {...props}>
      <RedeemDialog
        offer={{
          ...offer,
          usesCodes,
        }}
        state={dialogState}
        onOpenChange={() => setDialogState(DialogState.Closed)}
        redemption={redemptionState}
        error={embedError || error}
      />

      <Button
        theme="primary"
        fontWeight="500"
        size="$5"
        color="$offBlack"
        h={56}
        minWidth="$18"
        $gtSm={{ maxWidth: '$20' }}
        {...buttonProps}
        onPress={handlePress}
        loading={loading}
        disabled={loading}
      >
        {offer?.offerLinkLabel || (usesCodes ? 'Reveal Code & Website' : 'View Website')}
      </Button>
    </YStack>
  )
}
